import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import {
  getBusinessDetails,
  updateBusinessDetails,
  getDocuments,
  getDocumentTypes,
  uploadDocument,
  getBankDetails,
  changePassword
} from '../features/profile/profileSlice';
import {
  businessNameIcon,
  contactPersonIcon,
  contactNumberIcon,
  emailIcon,
  addressIcon,
  businessLogoIcon,
  bankNameIcon,
  accountNumberIcon,
  accountTypeIcon,
  bankingNameIcon,
  passwordIcon
} from '../assets';
import editPencilIcon from '../assets/images/Formicons/edit-pencil.svg';
import xMarkIcon from '../assets/x-mark.svg';
import CustomAlert from '../components/CustomAlert';
import AddressInput from '../components/AddressInput';
import "../css/profileSettings.css";
import checkedCheckboxIcon from '../assets/images/checked-checkbox-.svg';
import uncheckedCheckboxIcon from '../assets/images/uncheck-checkbox-outline.svg';
import { ShimmerBox } from '../components/Shimmer';
import '../css/Shimmer.css';

const ProfileSettings = () => {
  const dispatch = useDispatch();
  const { businessDetails, documents, documentTypes, bankDetails, isLoading, error } = useSelector((state) => state.profile);
  
  const [activeTab, setActiveTab] = useState("profile");
  const [showAddDocModal, setShowAddDocModal] = useState(false);
  const [showPasswordSuccessModal, setShowPasswordSuccessModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // 🆕 Change Document Modal State
  const [showChangeDocModal, setShowChangeDocModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [changeDocFile, setChangeDocFile] = useState(null);

  // Custom Alert State
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ type: 'success', title: '', message: '' });

  // Location State
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationCoords, setLocationCoords] = useState({
    latitude: '',
    longitude: '',
    city: '',
    state: '',
    pincode: ''
  });
  
  // Profile data state
  const [profileData, setProfileData] = useState({
    businessName: "",
    contactPerson: "",
    contactNumber: "",
    email: "",
    address: "",
    businessLogo: null
  });

  // Edit mode states
  const [editMode, setEditMode] = useState({
    contactNumber: false,
    email: false,
    address: false
  });

  // Temp values for editing
  const [tempValues, setTempValues] = useState({});

  // Add Document Form State - Dynamic based on API response
  const [addDocForm, setAddDocForm] = useState({
    selectedDocs: {},  // { doc_id: true/false }
    files: {},         // { doc_id: File }
    documentName: ""
  });

  // Security - Password
  const [passwords, setPasswords] = useState({
    current: "",
    new: "",
    confirm: ""
  });

  // Custom Alert Helper
  const showCustomAlert = (type, title, message) => {
    setAlertConfig({ type, title, message });
    setShowAlert(true);
  };

  // Load data on component mount and tab change
  useEffect(() => {
    if (activeTab === "profile") {
      dispatch(getBusinessDetails());
      dispatch(getDocuments());
      dispatch(getDocumentTypes());
    } else if (activeTab === "bank") {
      dispatch(getBankDetails());
    }
  }, [activeTab, dispatch]);

  // Update profileData when businessDetails changes
  useEffect(() => {
    if (businessDetails) {
      setProfileData({
        businessName: businessDetails.BusinessName || "",
        contactPerson: businessDetails.ContactPerson || "",
        contactNumber: businessDetails.ContactNumber || "",
        email: businessDetails.EmailId || "",
        address: businessDetails.Address || "",
        businessLogo: businessDetails.BusinessLogo || null
      });
    }
  }, [businessDetails]);

  // Handle edit toggle
  const toggleEdit = (field) => {
    if (editMode[field]) {
      setProfileData(prev => ({
        ...prev,
        [field]: tempValues[field]
      }));
      setEditMode(prev => ({ ...prev, [field]: false }));
    } else {
      setTempValues(prev => ({
        ...prev,
        [field]: profileData[field]
      }));
      setEditMode(prev => ({ ...prev, [field]: true }));
    }
  };

  // Handle input change
  const handleInputChange = (field, value) => {
    setTempValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle get current location
  const handleGetCurrentLocation = () => {
    setIsGettingLocation(true);

    if (!navigator.geolocation) {
      showCustomAlert('error', 'Not Supported', 'Geolocation is not supported by your browser');
      setIsGettingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`
          );
          const data = await response.json();

          if (data && data.display_name) {
            // Extract city, state, pincode from address details
            const addressDetails = data.address || {};
            const city = addressDetails.city || addressDetails.town || addressDetails.village || addressDetails.district || '';
            const state = addressDetails.state || '';
            const pincode = addressDetails.postcode || '';

            // Store all location data
            setLocationCoords({
              latitude: latitude.toString(),
              longitude: longitude.toString(),
              city: city,
              state: state,
              pincode: pincode
            });

            setTempValues(prev => ({
              ...prev,
              address: data.display_name
            }));
          } else {
            showCustomAlert('error', 'Error', 'Could not find address for your location');
          }
        } catch (error) {
          showCustomAlert('error', 'Error', 'Failed to get address. Please enter manually.');
        }

        setIsGettingLocation(false);
      },
      (error) => {
        setIsGettingLocation(false);

        switch (error.code) {
          case error.PERMISSION_DENIED:
            showCustomAlert('error', 'Permission Denied', 'Location permission denied. Please enter address manually.');
            break;
          case error.POSITION_UNAVAILABLE:
            showCustomAlert('error', 'Unavailable', 'Location unavailable. Please enter address manually.');
            break;
          case error.TIMEOUT:
            showCustomAlert('error', 'Timeout', 'Location request timed out. Please try again.');
            break;
          default:
            showCustomAlert('error', 'Error', 'Failed to get location. Please enter address manually.');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Handle logo upload
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileData(prev => ({
        ...prev,
        businessLogo: file
      }));
    }
  };

  // Handle Add Document Modal
  const handleOpenAddDoc = () => {
    setShowAddDocModal(true);
    document.body.style.overflow = 'hidden';
  };

  const handleCloseAddDoc = () => {
    setShowAddDocModal(false);
    document.body.style.overflow = 'unset';
    // Reset form
    setAddDocForm({
      selectedDocs: {},
      files: {},
      documentName: ""
    });
  };

  // Handle document type checkbox - now using doc id
  const handleDocTypeChange = (docId, isChecked) => {
    setAddDocForm(prev => ({
      ...prev,
      selectedDocs: {
        ...prev.selectedDocs,
        [docId]: isChecked
      }
    }));

    // Clear file when unchecked
    if (!isChecked) {
      setAddDocForm(prev => {
        const newFiles = { ...prev.files };
        delete newFiles[docId];
        return { ...prev, files: newFiles };
      });
    }
  };

  // Handle document file upload - now using doc id
  const handleDocFileUpload = (docId, file) => {
    if (file) {
      setAddDocForm(prev => ({
        ...prev,
        files: {
          ...prev.files,
          [docId]: file
        }
      }));
    }
  };

  // Handle Add Document Submit
  const handleAddDocSubmit = async () => {
    try {
      // Get selected document type IDs
      const selectedDocIds = Object.entries(addDocForm.selectedDocs)
        .filter(([id, isSelected]) => isSelected === true)
        .map(([id]) => parseInt(id));

      if (selectedDocIds.length === 0) {
        showCustomAlert('error', 'No Document Selected', 'Please select at least one document type');
        return;
      }

      let uploadedCount = 0;
      let failedUploads = [];

      // Upload each document
      for (const docId of selectedDocIds) {
        const file = addDocForm.files[docId];

        if (file) {
          try {
            const formData = new FormData();
            formData.append('DocumentTypeId', docId.toString());
            formData.append('Document', file);

            // Add CustomName if it's "Other" document type
            const docType = documentTypes.find(d => d.id === docId);
            if (docType?.code === 'OTHER' && addDocForm.documentName) {
              formData.append('CustomName', addDocForm.documentName);
            }

            await dispatch(uploadDocument(formData)).unwrap();
            uploadedCount++;
          } catch (uploadError) {
            const docType = documentTypes.find(d => d.id === docId);
            failedUploads.push(docType?.name || docId);
          }
        }
      }

      if (uploadedCount === 0) {
        showCustomAlert('error', 'Upload Failed', 'Failed to upload any documents. Please try again.');
        return;
      }

      // Close modal first
      handleCloseAddDoc();

      // Fetch fresh documents list from backend
      await dispatch(getDocuments()).unwrap();

      // Show success modal
      setShowSuccessModal(true);
      document.body.style.overflow = 'hidden';

    } catch (error) {
      showCustomAlert('error', 'Upload Failed', error || 'Failed to upload documents');
    }
  };

  // Handle profile save
  const handleProfileSave = async () => {
    try {
      // Use tempValues if in edit mode, otherwise use profileData
      const finalContactNumber = editMode.contactNumber ? tempValues.contactNumber : profileData.contactNumber;
      const finalEmail = editMode.email ? tempValues.email : profileData.email;
      const finalAddress = editMode.address ? tempValues.address : profileData.address;

      const formData = new FormData();
      formData.append('BusinessName', profileData.businessName);
      formData.append('ContactPerson', profileData.contactPerson);
      formData.append('BusinessPhone', finalContactNumber);
      formData.append('ContactNumber', finalContactNumber);
      formData.append('EmailId', finalEmail);
      formData.append('BusinessAddress', finalAddress);
      formData.append('Address', finalAddress);

      // Add location data if available
      if (locationCoords.latitude && locationCoords.longitude) {
        formData.append('Latitude', locationCoords.latitude);
        formData.append('Longitude', locationCoords.longitude);
        if (locationCoords.city) formData.append('City', locationCoords.city);
        if (locationCoords.state) formData.append('State', locationCoords.state);
        if (locationCoords.pincode) formData.append('Pincode', locationCoords.pincode);
      }

      if (profileData.businessLogo && typeof profileData.businessLogo !== 'string') {
        formData.append('BusinessLogo', profileData.businessLogo);
      }

      // Exit all edit modes after saving
      setEditMode({ contactNumber: false, email: false, address: false });

      // Update profileData with the values we're saving
      setProfileData(prev => ({
        ...prev,
        contactNumber: finalContactNumber,
        email: finalEmail,
        address: finalAddress
      }));

      await dispatch(updateBusinessDetails(formData)).unwrap();
      showCustomAlert('success', 'Success', 'Profile updated successfully!');

      // Refresh business details from backend
      await dispatch(getBusinessDetails()).unwrap();

    } catch (error) {
      showCustomAlert('error', 'Update Failed', error || 'Failed to update profile');
    }
  };

  // Handle password update
  const handlePasswordUpdate = async () => {
    if (!passwords.current || !passwords.new || !passwords.confirm) {
      showCustomAlert('error', 'Missing Fields', 'Please fill all password fields');
      return;
    }

    if (passwords.new !== passwords.confirm) {
      showCustomAlert('error', 'Password Mismatch', "New password and confirm password don't match!");
      return;
    }

    try {
      const passwordData = {
        CurrentPassword: passwords.current,
        NewPassword: passwords.new,
        ConfirmPassword: passwords.confirm
      };
      
      await dispatch(changePassword(passwordData)).unwrap();

      // Show success popup
      setShowPasswordSuccessModal(true);
      document.body.style.overflow = 'hidden';
      
      // Clear password fields
      setPasswords({ current: "", new: "", confirm: "" });

    } catch (error) {
      showCustomAlert('error', 'Change Failed', error || 'Failed to change password');
    }
  };

  // Handle close password success modal
  const handleClosePasswordSuccess = () => {
    setShowPasswordSuccessModal(false);
    document.body.style.overflow = 'unset';
  };

  // Handle close success modal
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    document.body.style.overflow = 'unset';
  };

  // Handle Open Change Document Modal
  const handleOpenChangeDoc = (doc) => {
    setSelectedDocument(doc);
    setChangeDocFile(null);
    setShowChangeDocModal(true);
    document.body.style.overflow = 'hidden';
  };

  // Handle Close Change Document Modal
  const handleCloseChangeDoc = () => {
    setShowChangeDocModal(false);
    setSelectedDocument(null);
    setChangeDocFile(null);
    document.body.style.overflow = 'unset';
  };

  // Handle Change Document Submit
  const handleChangeDocSubmit = async () => {
    if (!changeDocFile) {
      showCustomAlert('error', 'No File Selected', 'Please select a file to upload');
      return;
    }

    if (!selectedDocument) {
      showCustomAlert('error', 'No Document', 'No document selected');
      return;
    }

    try {
      const formData = new FormData();
      
      // Get DocumentTypeId - try multiple possible field names from API
      const documentTypeId = selectedDocument.document_type || 
                            selectedDocument.document_type_id || 
                            selectedDocument.DocumentTypeId ||
                            selectedDocument.DocumentType;
      
      if (!documentTypeId) {
        showCustomAlert('error', 'Missing Information', 'Document type information is missing. Please try again or contact support.');
        return;
      }

      formData.append('DocumentTypeId', documentTypeId.toString());
      formData.append('Document', changeDocFile);
      formData.append('DocumentId', selectedDocument.id.toString());

      await dispatch(uploadDocument(formData)).unwrap();

      // Close modal
      handleCloseChangeDoc();

      // Refresh documents list
      await dispatch(getDocuments()).unwrap();
      
      // Show success modal
      setShowSuccessModal(true);
      document.body.style.overflow = 'hidden';

    } catch (error) {
      showCustomAlert('error', 'Change Failed', error || 'Failed to change document');
    }
  };

  return (
    <div className="profile-settings-page">
      {/* Tabs Navigation - 3 TABS */}
      <div className="tabs-navigation">
        <button
          className={`tab-nav-btn ${activeTab === "profile" ? "active" : ""}`}
          onClick={() => setActiveTab("profile")}
        >
          Profile
        </button>
        <button
          className={`tab-nav-btn ${activeTab === "bank" ? "active" : ""}`}
          onClick={() => setActiveTab("bank")}
        >
          Bank Details
        </button>
        <button
          className={`tab-nav-btn ${activeTab === "security" ? "active" : ""}`}
          onClick={() => setActiveTab("security")}
        >
          Security
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        
        {/* Profile Tab */}
        {activeTab === "profile" && (
          <div className="profile-tab">
            {/* Business Details Section */}
            <div className="section-card">
              <h2 className="section-title">Business Details</h2>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className="text-label">Business Name</label>
                  <div className="input-with-icon">
                    <img src={businessNameIcon} alt="" className="input-icon" style={{ width: '20px', height: '20px' }} />
                    <input
                      type="text"
                      value={profileData.businessName}
                      readOnly
                      className="text-small-body"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="text-label">Contact Person</label>
                  <div className="input-with-icon">
                    <img src={contactPersonIcon} alt="" className="input-icon" style={{ width: '20px', height: '20px' }} />
                    <input
                      type="text"
                      value={profileData.contactPerson}
                      readOnly
                      className="text-small-body"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="text-label">Contact Number</label>
                  <div className="input-with-icon">
                    <img src={contactNumberIcon} alt="" className="input-icon" style={{ width: '20px', height: '20px' }} />
                    <input
                      type="tel"
                      value={editMode.contactNumber ? tempValues.contactNumber : profileData.contactNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                        handleInputChange('contactNumber', value);
                      }}
                      onKeyPress={(e) => {
                        if (!/[0-9]/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      maxLength={10}
                      readOnly={!editMode.contactNumber}
                      className="text-small-body"
                    />
                    <button
                      className="edit-btn"
                      onClick={() => toggleEdit('contactNumber')}
                      title={editMode.contactNumber ? "Save" : "Edit"}
                    >
                      {editMode.contactNumber ? '✓' : <img src={editPencilIcon} alt="Edit" />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label className="text-label">Email-Id</label>
                  <div className="input-with-icon">
                    <img src={emailIcon} alt="" className="input-icon" style={{ width: '20px', height: '20px' }} />
                    <input
                      type="email"
                      value={editMode.email ? tempValues.email : profileData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      readOnly={!editMode.email}
                      className="text-small-body"
                    />
                    <button
                      className="edit-btn"
                      onClick={() => toggleEdit('email')}
                      title={editMode.email ? "Save" : "Edit"}
                    >
                      {editMode.email ? '✓' : <img src={editPencilIcon} alt="Edit" />}
                    </button>
                  </div>
                </div>

                <div className="form-group address-form-group">
                  <label className="text-label">Address</label>
                  <div className="address-field-wrapper">
                    <AddressInput
                      value={editMode.address ? tempValues.address : profileData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      onBlur={() => {}}
                      placeholder="Search for address..."
                      name="address"
                      icon={addressIcon}
                      disabled={!editMode.address}
                      onLocationChange={(locationData) => {
                        setLocationCoords({
                          latitude: locationData.latitude?.toString() || '',
                          longitude: locationData.longitude?.toString() || '',
                          city: locationData.city || '',
                          state: locationData.state || '',
                          pincode: locationData.pincode || ''
                        });
                      }}
                    />
                    <button
                      className="edit-btn address-edit-btn"
                      onClick={() => toggleEdit('address')}
                      title={editMode.address ? "Save" : "Edit"}
                    >
                      {editMode.address ? '✓' : <img src={editPencilIcon} alt="Edit" />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label className="text-label">Business Logo</label>
                  <div className="logo-upload">
                    <img src={businessLogoIcon} alt="" className="logo-icon" style={{ width: '20px', height: '20px' }} />
                    <span className="logo-text text-small-body">
                      {profileData.businessLogo?.name || 
                       (typeof profileData.businessLogo === 'string' && profileData.businessLogo ? 'Logo Uploaded' : 'Business Logo')}
                    </span>
                    <label className="upload-btn text-small-body-medium">
                      Choose File
                      <input type="file" accept="image/*" onChange={handleLogoUpload} />
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* License Information Section */}
            <div className="section-card">
              <div className="section-header">
                <h2 className="section-title">License Information</h2>
                <button className="add-doc-btn text-button" onClick={handleOpenAddDoc}>
                  ADD DOCUMENT
                </button>
              </div>

              {/* Show error message if documents fetch fails */}
              {error && (
                <div className="error-banner" style={{
                  padding: '12px',
                  backgroundColor: '#fee',
                  color: '#c00',
                  borderRadius: '8px',
                  marginBottom: '16px'
                }}>
                  <strong>Error:</strong> {error}
                </div>
              )}

              <div className="license-grid">
                {isLoading ? (
                  <div className="shimmer-license-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' }}>
                    {[1, 2, 3, 4].map((item) => (
                      <div key={item} className="license-card" style={{ padding: '16px' }}>
                        <ShimmerBox width="140px" height="16px" />
                        <ShimmerBox width="100%" height="100px" borderRadius="8px" style={{ marginTop: '12px' }} />
                        <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
                          <ShimmerBox width="60px" height="28px" borderRadius="4px" />
                          <ShimmerBox width="60px" height="28px" borderRadius="4px" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : documents && documents.length > 0 ? (
                  documents.map((doc) => (
                    <div key={doc.id} className="license-card">
                      <h3 className="text-small-body-medium">{doc.document_name}</h3>
                      <div className="document-preview">
                        {doc.document_url && (
                          <img 
                            src={doc.document_url} 
                            alt={doc.document_name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:#999;">📄</div>';
                            }}
                          />
                        )}
                      </div>
                      <div className="license-actions">
                        <button
                          className="change-btn text-small-body-medium"
                          onClick={() => handleOpenChangeDoc(doc)}
                        >
                          Change
                        </button>
                        <a
                          href={doc.document_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="view-btn text-small-body-medium"
                        >
                          View
                        </a>
                      </div>
                    </div>
                  ))
                ) : (
                  <p>No documents uploaded yet.</p>
                )}
              </div>
            </div>

            <button 
              className="submit-btn text-button" 
              onClick={handleProfileSave}
              disabled={isLoading}
            >
              {isLoading ? 'SAVING...' : 'SAVE & SUBMIT'}
            </button>
          </div>
        )}

        {/* Bank Details Tab */}
        {activeTab === "bank" && (
          <div className="bank-tab">
            <div className="section-card">
              <h2 className="section-title">Bank Details</h2>
              
              <div className="info-banner">
                <span className="info-icon">⚠️</span>
                <p className="text-small-body">
                  If you wish to change the bank details, please email to admin@xtrafyl.com and our team will contact you shortly.
                </p>
              </div>

              {isLoading ? (
                <div className="form-grid">
                  {[1, 2, 3, 4].map((item) => (
                    <div key={item} className="form-group">
                      <ShimmerBox width="100px" height="14px" />
                      <div className="input-with-icon" style={{ marginTop: '8px' }}>
                        <ShimmerBox width="20px" height="20px" borderRadius="4px" />
                        <ShimmerBox width="100%" height="40px" borderRadius="8px" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : bankDetails ? (
                <div className="form-grid">
                  <div className="form-group">
                    <label className="text-label">Bank Name</label>
                    <div className="input-with-icon">
                      <img src={bankNameIcon} alt="" className="input-icon" style={{ width: '20px', height: '20px' }} />
                      <input
                        type="text"
                        value={bankDetails.BankName || 'Not set'}
                        readOnly
                        className="text-small-body"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="text-label">Account Number</label>
                    <div className="input-with-icon">
                      <img src={accountNumberIcon} alt="" className="input-icon" style={{ width: '20px', height: '20px' }} />
                      <input
                        type="text"
                        value={bankDetails.AccountNumber || 'Not set'}
                        readOnly
                        className="text-small-body"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="text-label">Account Type</label>
                    <div className="input-with-icon">
                      <img src={accountTypeIcon} alt="" className="input-icon" style={{ width: '20px', height: '20px' }} />
                      <input
                        type="text"
                        value={bankDetails.AccountType || 'Not set'}
                        readOnly
                        className="text-small-body"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="text-label">Account Holder Name</label>
                    <div className="input-with-icon">
                      <img src={bankingNameIcon} alt="" className="input-icon" style={{ width: '20px', height: '20px' }} />
                      <input
                        type="text"
                        value={bankDetails.AccountHolderName || 'Not set'}
                        readOnly
                        className="text-small-body"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <p>No bank details available.</p>
              )}
            </div>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === "security" && (
          <div className="security-tab">
            <div className="section-card">
              <h2 className="section-title">Password</h2>

              <div className="form-group-full">
                <label className="text-label">Current Password</label>
                <div className="input-with-icon">
                  <img src={passwordIcon} alt="" className="input-icon" style={{ width: '20px', height: '20px' }} />
                  <input
                    type="password"
                    placeholder="Current Password"
                    value={passwords.current}
                    onChange={(e) => setPasswords({...passwords, current: e.target.value})}
                    className="text-small-body"
                  />
                </div>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label className="text-label">New Password</label>
                  <div className="input-with-icon">
                    <img src={passwordIcon} alt="" className="input-icon" style={{ width: '20px', height: '20px' }} />
                    <input
                      type="password"
                      placeholder="New Password"
                      value={passwords.new}
                      onChange={(e) => setPasswords({...passwords, new: e.target.value})}
                      className="text-small-body"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="text-label">Confirm Password</label>
                  <div className="input-with-icon">
                    <img src={passwordIcon} alt="" className="input-icon" style={{ width: '20px', height: '20px' }} />
                    <input
                      type="password"
                      placeholder="Confirm Password"
                      value={passwords.confirm}
                      onChange={(e) => setPasswords({...passwords, confirm: e.target.value})}
                      className="text-small-body"
                    />
                  </div>
                </div>
              </div>

              <button 
                className="submit-btn text-button profile_pass" 
                onClick={handlePasswordUpdate}
                disabled={isLoading}
              >
                {isLoading ? 'UPDATING...' : 'UPDATE PASSWORD'}
              </button>
            </div>
          </div>
        )}

      </div>

      {/* ADD DOCUMENT MODAL */}
      {showAddDocModal && (
        <div className="modal-overlay" onClick={handleCloseAddDoc}>
          <div className="add-doc-modal" onClick={(e) => e.stopPropagation()}>

            <button className="modal-close-btn" onClick={handleCloseAddDoc} title="Close">
              <img src={xMarkIcon} alt="Close" />
            </button>

            <div className="modal-section">
              <h3 className="modal-section-title">Document Type</h3>

              <div className="doc-types-list">
                {documentTypes && documentTypes.length > 0 ? (
                  documentTypes
                    .filter(doc => doc.is_active)
                    .map(doc => (
                      <div key={doc.id} className="document-item">
                        <label className="doc-checkbox-label custom-checkbox">
                          <input
                            type="checkbox"
                            checked={addDocForm.selectedDocs[doc.id] || false}
                            onChange={(e) => handleDocTypeChange(doc.id, e.target.checked)}
                            className="doc-checkbox"
                          />
                          <img src={checkedCheckboxIcon} alt="" className="checkbox-icon checked" />
                          <img src={uncheckedCheckboxIcon} alt="" className="checkbox-icon unchecked" />
                          <span className="doc-label-text text-small-body">{doc.name}</span>
                        </label>

                        {addDocForm.selectedDocs[doc.id] && (
                          <label className="upload-btn-inline text-small-body-medium">
                            Upload
                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={(e) => handleDocFileUpload(doc.id, e.target.files[0])}
                              hidden
                            />
                          </label>
                        )}

                        {addDocForm.files[doc.id] && (
                          <span className="file-uploaded-name text-small-label">
                            {addDocForm.files[doc.id].name}
                          </span>
                        )}
                      </div>
                    ))
                ) : (
                  <p className="text-small-body">Loading document types...</p>
                )}

                {/* Show document name input for "Other" type */}
                {documentTypes && documentTypes.find(d => d.code === 'OTHER' && addDocForm.selectedDocs[d.id]) && (
                  <div className="doc-name-input">
                    <span className="doc-name-icon">📄</span>
                    <input
                      type="text"
                      placeholder="Document Name"
                      value={addDocForm.documentName}
                      onChange={(e) => setAddDocForm({...addDocForm, documentName: e.target.value})}
                      className="text-small-body"
                    />
                  </div>
                )}
              </div>
            </div>

            <button
              className="modal-submit-btn text-button"
              onClick={handleAddDocSubmit}
              disabled={isLoading}
            >
              {isLoading ? 'UPLOADING...' : 'SAVE & SUBMIT'}
            </button>
          </div>
        </div>
      )}

      {/* SUCCESS MODAL */}
      {showSuccessModal && (
        <div className="modal-overlay" onClick={handleCloseSuccessModal}>
          <div className="password-success-modal" onClick={(e) => e.stopPropagation()}>
            
            <button className="modal-close-btn" onClick={handleCloseSuccessModal} title="Close">
              <img src={xMarkIcon} alt="Close" />
            </button>

            <div className="success-icon-wrapper">
              <div className="success-icon">
                <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
                  <circle cx="60" cy="60" r="60" fill="url(#successGradient)"/>
                  <path d="M35 60 L52 77 L85 44" stroke="white" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round"/>
                  <defs>
                    <linearGradient id="successGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#8B5CF6"/>
                      <stop offset="100%" stopColor="#6366F1"/>
                    </linearGradient>
                  </defs>
                </svg>
              </div>
            </div>

            <h3 className="success-title">Documents Uploaded Successfully</h3>

          </div>
        </div>
      )}

      {/* PASSWORD SUCCESS MODAL */}
      {showPasswordSuccessModal && (
        <div className="modal-overlay" onClick={handleClosePasswordSuccess}>
          <div className="password-success-modal" onClick={(e) => e.stopPropagation()}>
            
            <button className="modal-close-btn" onClick={handleClosePasswordSuccess} title="Close">
              <img src={xMarkIcon} alt="Close" />
            </button>

            <div className="success-icon-wrapper">
              <div className="success-icon">
                <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
                  <circle cx="60" cy="60" r="60" fill="url(#successGradient2)"/>
                  <path d="M35 60 L52 77 L85 44" stroke="white" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round"/>
                  <defs>
                    <linearGradient id="successGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#8B5CF6"/>
                      <stop offset="100%" stopColor="#6366F1"/>
                    </linearGradient>
                  </defs>
                </svg>
              </div>
            </div>

            <h3 className="success-title">Password Updated Successfully</h3>

          </div>
        </div>
      )}

      {/* 🆕 CHANGE DOCUMENT MODAL */}
      {showChangeDocModal && selectedDocument && (
        <div className="modal-overlay" onClick={handleCloseChangeDoc}>
          <div className="change-doc-modal" onClick={(e) => e.stopPropagation()}>
            
            <button className="modal-close-btn" onClick={handleCloseChangeDoc} title="Close">
              <img src={xMarkIcon} alt="Close" />
            </button>

            <div className="modal-section">
              <h3 className="modal-section-title">Change Document</h3>
              <p className="modal-subtitle">
                Update your <strong>{selectedDocument.document_name}</strong>
              </p>
              
              {/* Current Document Preview */}
              <div className="current-doc-preview">
                <p className="preview-label">Current Document:</p>
                <div className="preview-card">
                  {selectedDocument.document_url && (
                    <img 
                      src={selectedDocument.document_url} 
                      alt={selectedDocument.document_name}
                      style={{ width: '100%', height: '200px', objectFit: 'cover', borderRadius: '8px' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:200px;color:#999;background:#F8FAFC;border-radius:8px;">📄</div>';
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Upload New Document */}
              <div className="upload-new-doc">
                <p className="upload-label">Upload New Document:</p>
                <div className="file-upload-box">
                  <input
                    type="file"
                    id="changeDocFile"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setChangeDocFile(file);
                      }
                    }}
                    hidden
                  />
                  <label htmlFor="changeDocFile" className="file-upload-label">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                      <path d="M33.3333 25V31.6667C33.3333 32.5507 32.9821 33.3986 32.357 34.0237C31.7319 34.6488 30.8841 35 30 35H10C9.11594 35 8.2681 34.6488 7.64298 34.0237C7.01786 33.3986 6.66667 32.5507 6.66667 31.6667V25" stroke="#6366F1" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M26.6667 13.3333L20 6.66666L13.3333 13.3333" stroke="#6366F1" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M20 6.66666V25" stroke="#6366F1" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span className="upload-text">
                      {changeDocFile ? changeDocFile.name : 'Click to upload or drag and drop'}
                    </span>
                    <span className="upload-hint">PDF, JPG, JPEG or PNG (Max 10MB)</span>
                  </label>
                </div>
              </div>
            </div>

            <button 
              className="modal-submit-btn text-button" 
              onClick={handleChangeDocSubmit}
              disabled={isLoading || !changeDocFile}
            >
              {isLoading ? 'UPLOADING...' : 'SAVE CHANGES'}
            </button>
          </div>
        </div>
      )}

      {/* Custom Alert */}
      <CustomAlert
        show={showAlert}
        onClose={() => setShowAlert(false)}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
      />
    </div>
  );
};

export default ProfileSettings;